import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/client/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/client/components/ui/card";
import { api, describeError, unwrap } from "@/client/lib/api";
import { loadStories } from "@/client/db";

interface Invitation {
  id: string;
  storyId: string;
  storyName: string;
  invitedByName: string;
}

/** Story invitations waiting for the signed-in user, shown above their projects. */
export function InvitationsList() {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setInvitations(await unwrap(api.api.invitations.get()));
    } catch (error) {
      console.error("[Invitations] Load failed:", error);
    }
  }, []);

  useEffect(() => {
    void load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load]);

  const respond = async (invitation: Invitation, accept: boolean) => {
    setBusy(invitation.id);
    try {
      if (accept) {
        await unwrap(api.api.invitations({ id: invitation.id }).accept.post());
        await loadStories();
        await navigate({ to: "/story/$storyId", params: { storyId: invitation.storyId } });
      } else {
        await unwrap(api.api.invitations({ id: invitation.id }).decline.post());
      }
    } catch (error) {
      toast.error(accept ? "Couldn't accept the invitation" : "Couldn't decline the invitation", {
        description: describeError(error),
      });
    } finally {
      setBusy(null);
      await load();
    }
  };

  if (invitations.length === 0) return null;

  return (
    <section className="mb-8 grid gap-3">
      <h2 className="text-sm font-medium text-muted-foreground">Invitations</h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {invitations.map((invitation) => (
          <Card key={invitation.id} className="border-dashed">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg">{invitation.storyName}</CardTitle>
              </div>
              <CardDescription>{invitation.invitedByName} invited you to edit this story.</CardDescription>
              <div className="mt-3 flex gap-2">
                <Button size="sm" disabled={busy !== null} onClick={() => void respond(invitation, true)}>
                  {busy === invitation.id && <Loader2 className="animate-spin" />}
                  Accept
                </Button>
                <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void respond(invitation, false)}>
                  Decline
                </Button>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </section>
  );
}
